import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  Typography
} from '@mui/material';
import { AddAPhoto as AddPhotoIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material';

/**
 * Componente de formulário para dados pessoais do usuário
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.formData - Dados do formulário
 * @param {Function} props.handleInputChange - Função para atualizar o formulário
 * @param {Function} props.handleImageSelect - Função para selecionar imagem
 * @param {string|null} props.previewImage - URL da imagem de preview
 * @param {Function} props.handleImageError - Função para tratar erros de imagem
 * @param {boolean} props.editingId - ID do usuário sendo editado ou null
 * @returns {JSX.Element}
 */
const DadosPessoaisForm = ({
  formData,
  handleInputChange,
  handleImageSelect,
  previewImage,
  handleImageError,
  editingId
}) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 4, 
      alignItems: 'flex-start',
      flexDirection: { xs: 'column', sm: 'row' }
    }}>
      {/* Coluna da Esquerda */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        width: { xs: '100%', sm: 'auto' }
      }}>
        <Box
          sx={{
            position: 'relative',
            mb: 3,
            width: 160,
            height: 160,
            borderRadius: '50%',
            border: previewImage ? 'none' : '2px dashed',
            borderColor: previewImage ? 'transparent' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            boxShadow: previewImage ? '0 4px 20px rgba(0, 0, 0, 0.12)' : 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 6px 25px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          {previewImage ? (
            <Avatar
              src={previewImage}
              alt="Foto de perfil"
              sx={{ 
                width: '100%', 
                height: '100%',
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                '& .MuiAvatar-img': {
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%'
                }
              }}
              imgProps={{
                onError: (e) => {
                  console.log('=== DadosPessoaisForm - Erro de Imagem INÍCIO ===');
                  console.log('Erro ao carregar imagem no formulário:', e.target.src);
                  
                  // Usar imagem padrão em formato base64
                  const { getDefaultImage } = require('../../utils/formatters');
                  e.target.src = getDefaultImage();
                  console.log('Imagem padrão aplicada no formulário');
                  console.log('=== DadosPessoaisForm - Erro de Imagem FIM ===');
                }
              }}
            >
              <AccountCircleIcon sx={{ fontSize: 60 }} />
            </Avatar>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: 'primary.main',
                opacity: 0.8,
                transition: 'opacity 0.3s ease',
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              <AddPhotoIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography 
                variant="caption" 
                align="center"
                sx={{ fontWeight: 500 }}
              >
                Adicionar foto
              </Typography>
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
            }}
          />
        </Box>

        <TextField
          fullWidth
          label="Nome Completo"
          name="nome"
          value={formData.nome}
          onChange={handleInputChange}
          margin="normal"
          required
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: '10px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          margin="normal"
          required
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: '10px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }
          }}
        />
      </Box>

      {/* Divisor Vertical */}
      <Divider orientation="vertical" flexItem sx={{ 
        height: 'auto', 
        alignSelf: 'stretch',
        display: { xs: 'none', sm: 'block' }
      }} />

      {/* Divisor Horizontal para telas pequenas */}
      <Divider sx={{ 
        width: '100%', 
        my: 2, 
        display: { xs: 'block', sm: 'none' } 
      }} />

      {/* Coluna da Direita */}
      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
        <TextField
          fullWidth
          label="Nome de Usuário"
          name="usuario"
          value={formData.usuario}
          onChange={handleInputChange}
          margin="normal"
          required
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: '10px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Senha"
          name="senha"
          type="password"
          value={formData.senha}
          onChange={handleInputChange}
          margin="normal"
          required={!editingId}
          helperText={editingId ? "Deixe em branco para manter a senha atual" : ""}
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: '10px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }
          }}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
          <InputLabel>Cargo</InputLabel>
          <Select
            name="cargo"
            value={formData.cargo}
            onChange={handleInputChange}
            label="Cargo"
            sx={{ 
              borderRadius: '10px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}
          >
            <MenuItem value="ATENDENTE">Atendente</MenuItem>
            <MenuItem value="ADMIN">Administrador</MenuItem>
            <MenuItem value="RECEPCAO">Recepção</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Guichê"
          name="guiche"
          type="number"
          value={formData.guiche}
          onChange={handleInputChange}
          margin="normal"
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: '10px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default DadosPessoaisForm; 